import { z } from "zod";
import {
  AccommodationReservationClientDtoSchema,
  AccommodationReservationClientFromDto,
  AccommodationReservationClientSchema,
  AccommodationReservationClientToDto,
} from "./accommodation-reservation-client";
import { apiListSchema } from "./list";
import { HotelRoomPriceSchema } from "./hotel-room-price";
//? DTO
export const CreateAccommodationDtoSchema = z.object({
  Day: z.number().nullish(),
  RequestedType: z.number().nullish(),
  State: z.string().nullish(),
  ChildrenCount: z.number().nullish(),
  HeadCount: z.number().nullish(),
  NumberOfBeds: z.number().nullish(),
  HotelRoomPriceId: z.number().nullish(),
  HotelRoomId: z.number().nullish(),
  Notes: z.string().nullish(),
  Release: z.string().nullish(),
  Expenses: z.number().nullish(),
  InvoiceNumber: z.string().nullish(),
  AccommodationReservationClients: z
    .array(AccommodationReservationClientDtoSchema)
    .nullish(),
  ReservationId: z.number().nullish(),
});

export type CreateAccommodationDto = z.infer<
  typeof CreateAccommodationDtoSchema
>;

export const AccommodationDtoSchema = CreateAccommodationDtoSchema.extend({
  Id: z.number(),
  HotelRoomPrice: HotelRoomPriceSchema.nullish(),
});

export type AccommodationDto = z.infer<typeof AccommodationDtoSchema>;

export const AccommodationDtoListSchema = apiListSchema(AccommodationDtoSchema);

export enum AccommodationState {
  No = "No",
  SentToHotel = "SentToHotel",
  Ongoing = "Ongoing",
  Phone = "Phone",
  PreBooking = "PreBooking",
  Ok = "Ok",
  OkPromo = "OkPromo",
  SelfBooked = "SelfBooked",
  BookingCom = "BookingCom",
  Canceled = "Canceled",
}

export const AccommodationStateAsArray = Object.values(AccommodationState);

export enum AccommodationRequestedType {
  requestedType = "requestedType",
  requestedType1 = "requestedType1",
}

export const accommodationRequestedTypeEnumToNumber: Record<
  AccommodationRequestedType,
  number
> = {
  requestedType: 0,
  requestedType1: 1,
};

export const accommodationRequestedTypeNumToEnum = Object.entries(
  accommodationRequestedTypeEnumToNumber,
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  AccommodationRequestedType
>;

export const AccommodationRequestedTypeAsArray = Object.values(
  AccommodationRequestedType,
);

export const CreateAccommodationSchema = z.object({
  day: z.number(),
  requestedType: z.nativeEnum(AccommodationRequestedType).nullish(),
  state: z.nativeEnum(AccommodationState).nullish(),
  childrenCount: z.number().nullish(),
  headCount: z.number().nullish(),
  numberOfBeds: z.number().nullish(),
  hotelRoomPriceId: z.string().nullish(),
  hotelRoomId: z.number().nullish(),
  notes: z.string().nullish(),
  release: z.date().nullish(),
  expenses: z.number().nullish(),
  invoiceNumber: z.string().nullish(),
  reservationClients: z.array(AccommodationReservationClientSchema),
  reservationId: z.number().nullish(),
});
export type CreateAccommodation = z.infer<typeof CreateAccommodationSchema>;

export const AccommodationSchema = CreateAccommodationSchema.extend({
  id: z.string().min(1),
  hotelRoomPrice: HotelRoomPriceSchema.nullish(),
});
export type Accommodation = z.infer<typeof AccommodationSchema>;

//? Mappers

export function accommodationFromDto(dto: AccommodationDto): Accommodation {
  return {
    id: dto.Id.toString(),
    day: dto.Day ?? -1,
    requestedType:
      typeof dto.RequestedType === "number"
        ? accommodationRequestedTypeNumToEnum[dto.RequestedType]
        : undefined,
    state: dto.State ? (dto.State as AccommodationState) : undefined,
    childrenCount: dto.ChildrenCount,
    headCount: dto.HeadCount,
    numberOfBeds: dto.NumberOfBeds,
    hotelRoomPriceId: dto.HotelRoomPriceId?.toString(),
    hotelRoomId: dto.HotelRoomId,
    notes: dto.Notes,
    release: dto.Release ? new Date(dto.Release) : undefined,
    expenses: dto.Expenses,
    invoiceNumber: dto.InvoiceNumber,
    reservationClients:
      dto.AccommodationReservationClients?.map(
        AccommodationReservationClientFromDto,
      ) ?? [],
    reservationId: dto.ReservationId,
    hotelRoomPrice: dto.HotelRoomPrice,
  };
}

export function accommodationToDto(dto: Accommodation): AccommodationDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createAccommodationToDto(dto, dto.id),
  };
}

export function createAccommodationToDto(
  dto: CreateAccommodation,
  id?: string,
): CreateAccommodationDto {
  return {
    Day: dto.day,
    RequestedType: dto.requestedType
      ? accommodationRequestedTypeEnumToNumber[dto.requestedType]
      : undefined,
    State: dto.state,
    ChildrenCount: dto.childrenCount,
    HeadCount: dto.headCount,
    NumberOfBeds: dto.numberOfBeds,
    HotelRoomPriceId: dto.hotelRoomPriceId
      ? Number.parseInt(dto.hotelRoomPriceId)
      : undefined,
    HotelRoomId: dto.hotelRoomId,
    Notes: dto.notes,
    Release: dto.release?.toDateString(),
    Expenses: dto.expenses,
    InvoiceNumber: dto.invoiceNumber,
    ReservationId: dto.reservationId,
    AccommodationReservationClients: id
      ? dto.reservationClients.map(AccommodationReservationClientToDto)
      : undefined,
  };
}
